import type { AlertMetric, AlertSeverity, AlertView } from '@occ/contracts';

/**
 * Lectura de alertas para la vista de mapa (sección 7.1/7.2/7.3 del spec).
 *
 * Separado de `color.ts`: aquí se decide **cuál** alerta importa y cómo se
 * redacta, allá cómo se pinta. `buildGraph` combina los dos.
 */

export const METRIC_LABEL: Record<AlertMetric, string> = {
  node_offline: 'nodo caído',
  cpu_percent: 'CPU',
  memory_percent: 'memoria',
  disk_percent: 'disco',
  service_down: 'servicio caído',
  process_down: 'proceso caído',
  container_down: 'contenedor caído',
};

const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 2, warning: 1, info: 0 };

/** La de mayor severidad entre las abiertas de un nodo (7.1: "la de mayor severity"). */
export function worstAlert(alerts: AlertView[]): AlertView | null {
  return alerts.reduce<AlertView | null>((worst, alert) => {
    if (!worst) return alert;
    return SEVERITY_RANK[alert.severity] > SEVERITY_RANK[worst.severity] ? alert : worst;
  }, null);
}

export function worstSeverity(alerts: AlertView[]): AlertSeverity | null {
  return worstAlert(alerts)?.severity ?? null;
}

/** "92% — umbral 80%": solo tiene sentido para métricas en porcentaje (sección 7.1). */
export function formatMetricValue(
  alert: Pick<AlertView, 'metric' | 'value' | 'threshold'>,
): string | null {
  if (!alert.metric.endsWith('_percent') || alert.value === null) return null;
  return `${Math.round(alert.value)}% — umbral ${Math.round(alert.threshold)}%`;
}

export const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: 'Crítica',
  warning: 'Warning',
  info: 'Info',
};

export const SEVERITY_BADGE_CLASSES: Record<AlertSeverity, string> = {
  critical: 'border-red-500/60 text-red-600 dark:text-red-400',
  warning: 'border-amber-500/60 text-amber-700 dark:text-amber-400',
  info: 'border-sky-500/50 text-sky-700 dark:text-sky-400',
};
